import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// "All Units" mode: a pseudo-unit for full-scope roles (admin/md/hr).
// When active, the unit query param is stripped so list endpoints return
// every unit (backend still enforces scope server-side for locked roles).
// NOTE: never create a real unit literally named "All Units".
export const ALL_UNITS = 'All Units';

// Resolve a media URL that may be:
//  - null/empty → null
//  - a data: URI (legacy inline photo) → returned as-is so it still renders
//  - an absolute URL → returned as-is
//  - a relative "/api/files/..." path from the backend → prefixed with the
//    backend origin so <img>/<a> resolve correctly regardless of frontend host
export const resolveMediaUrl = (url) => {
  if (!url || typeof url !== 'string') return null;
  if (url.startsWith('data:') || url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/api/')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
};

api.interceptors.request.use((config) => {
  if (config.params && config.params.unit === ALL_UNITS) {
    const { unit, ...rest } = config.params;
    config.params = rest;
  }
  return config;
});

let refreshing = null;
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    // Normalize FastAPI validation errors (422 detail is an array/object) into a
    // string so no component can render it as a React child and crash the page.
    const _d = error.response?.data?.detail;
    if (_d !== undefined && typeof _d !== 'string') {
      error.response.data.detail = Array.isArray(_d)
        ? _d.map((x) => (x && typeof x.msg === 'string' ? x.msg : JSON.stringify(x))).join(' ')
        : (_d && typeof _d.msg === 'string' ? _d.msg : JSON.stringify(_d));
    }
    const original = error.config;
    const status = error.response?.status;
    const isAuthCall = original?.url?.includes('/auth/');
    if (status === 401 && !original._retry && !isAuthCall) {
      original._retry = true;
      try {
        refreshing = refreshing || api.post('/auth/refresh');
        await refreshing;
        refreshing = null;
        return api(original);
      } catch (e) {
        refreshing = null;
        return Promise.reject(e);
      }
    }
    return Promise.reject(error);
  }
);

export const employeeApi = {
  list: (params) => api.get('/employees', { params }),
  exList: (params) => api.get('/ex-employees', { params }),
  managers: (params) => api.get('/employees/managers', { params }),
  get: (id) => api.get(`/employees/${id}`),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  transfer: (id, data) => api.post(`/employees/${id}/transfer`, data),
  // The one action that processes an exit. Not part of the edit form: an exit
  // has its own dates and its own consequences (notice period, vacancy,
  // Requirement TAT), and a mandatory Reason of Leaving.
  resign: (id, data) => api.post(`/employees/${id}/resign`, data),
  cancelResignation: (id) => api.post(`/employees/${id}/cancel-resignation`),
  deactivate: (id, keepAsVacancy = false) => api.post(`/employees/${id}/deactivate`, null, { params: { keep_as_vacancy: keepAsVacancy } }),
  uploadPhoto: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/employees/${id}/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  removePhoto: (id) => api.delete(`/employees/${id}/photo`),
  promotionLetter: (id, data) => api.post(`/employees/${id}/promotion-letter`, data),
  convertToEmployee: (id, data) => api.post(`/admin/employees/${id}/convert-to-employee`, data || {}),
  incrementLetter: (id, data) => api.post(`/employees/${id}/increment-letter`, data),
  transferLetter: (id, data) => api.post(`/employees/${id}/transfer-letter`, data),
  setCorporate: (id, enabled) => api.post(`/employees/${id}/corporate`, { enabled }),
  remove: (id) => api.delete(`/employees/${id}`),
  trash: (id) => api.post(`/employees/${id}/trash`),
  restore: (id) => api.post(`/employees/${id}/restore`),
  trashList: () => api.get('/trash'),
  kpiLink: (id) => api.get(`/employees/${id}/kpi-link`),
  noDues: (id, data) => api.post(`/employees/${id}/no-dues`, data),
  // Source-of-hiring and experience vocabularies. Served from the backend so
  // the add form, the edit form and the filter bar cannot drift apart.
  // Not under /employees/ — that prefix has a "/{emp_id}" route that would
  // capture the path segment as an id.
  hiringMeta: () => api.get('/meta/hiring'),
};

export const vacancyApi = {
  list: (params) => api.get('/vacancies', { params }),
  create: (data) => api.post('/vacancies', data),
  update: (id, data) => api.put(`/vacancies/${id}`, data),
  remove: (id) => api.delete(`/vacancies/${id}`),
  generateJd: (id, regenerate = false) => api.post(`/vacancies/${id}/jd`, null, { params: { regenerate } }),
};

export const salaryApi = {
  get: (empId) => api.get(`/employees/${empId}/salary`),
  update: (empId, year, data) => api.put(`/employees/${empId}/salary/${year}`, data),
  revise: (empId, year, data) => api.post(`/employees/${empId}/salary/${year}/revision`, data),
  backfill: (empId, data) => api.post(`/employees/${empId}/salary/backfill`, data),
  deleteYear: (empId, year) => api.delete(`/employees/${empId}/salary/${year}`),
};

// The HR-F-42 salary break-up: the CTC STRUCTURE an employee is on, which is
// a different thing from `salaryApi` above (the year-by-year gross history)
// and from monthly payroll. Each save is a new effective-dated revision; the
// previous structure is never overwritten.
export const salaryBreakupApi = {
  // One row per year in Salary Progression, with that year's break-up state.
  // The years come from the salary history itself — this never keeps its own.
  years: (empId) => api.get(`/employees/${empId}/salary-breakup/years`),
  get: (empId, year, revision) => api.get(`/employees/${empId}/salary-breakup`,
                                          { params: { year, revision } }),
  save: (empId, data) => api.post(`/employees/${empId}/salary-breakup`, data),
  sheet: (empId, year, revision, logoUrl) =>
    api.get(`/employees/${empId}/salary-breakup/sheet`,
            { params: { year, revision, logo_url: logoUrl } }),
  components: () => api.get('/salary-components'),
  addComponent: (data) => api.post('/salary-components', data),
  updateComponent: (key, data) => api.put(`/salary-components/${key}`, data),
};

export const salaryMatrixApi = {
  get: (params) => api.get('/salary-matrix', { params }),
};

// Criteria — admin-defined categories and marking rules. Deliberately separate
// from `kpiApi`: nothing here is read by the scoring engine, and keeping the
// calls apart keeps that boundary visible.
export const criteriaApi = {
  categories: (params) => api.get('/kpi-categories', { params }),
  createCategory: (data) => api.post('/kpi-categories', data),
  updateCategory: (id, data) => api.put(`/kpi-categories/${id}`, data),
  deleteCategory: (id, params) => api.delete(`/kpi-categories/${id}`, { params }),
  list: (params) => api.get('/kpi-criteria', { params }),
  create: (data) => api.post('/kpi-criteria', data),
  update: (id, data) => api.put(`/kpi-criteria/${id}`, data),
  remove: (id) => api.delete(`/kpi-criteria/${id}`),
  validate: (data) => api.post('/kpi-criteria/validate', data),
};

// Category → KPI routing. Three separate setters on purpose: category,
// destination and metric are different decisions with different consequences,
// and an endpoint that could do all three would make a filing change able to
// alter which number a KPI reads.
export const routeApi = {
  list: (params) => api.get('/kpi-routes', { params }),
  destinations: (params) => api.get('/kpi-routes/destinations', { params }),
  create: (data) => api.post('/kpi-routes', data),
  setCategory: (id, data) => api.put(`/kpi-routes/${id}/category`, data),
  setDestination: (id, data) => api.put(`/kpi-routes/${id}/destination`, data),
  setMetric: (id, data) => api.put(`/kpi-routes/${id}/metric`, data),
  retire: (id) => api.delete(`/kpi-routes/${id}`),
  migrate: () => api.post('/kpi-routes/migrate'),
};

export const kpiApi = {
  get: (empId, fy) => api.get(`/employees/${empId}/kpi`, { params: { fy } }),
  update: (empId, data) => api.post(`/employees/${empId}/kpi`, data),
  tokens: (params) => api.get('/admin/kpi-tokens', { params }),
  kpiLink: (empId) => api.get(`/employees/${empId}/kpi-link`),
  pending: () => api.get('/kpi-pending'),
  // The Accident register's four severities. Kept apart from the ordinary
  // marking ladder because they are not one: four counts produce one mark.
  accidentLadders: (unit) => api.get('/kpi/accident-ladders', { params: unit ? { unit } : {} }),
  saveAccidentLadders: (body) => api.put('/kpi/accident-ladders', body),
  templates: () => api.get('/kpi-templates'),
  seedTemplates: (overwrite = false) => api.post('/admin/kpi-templates/seed-defaults', null, { params: { overwrite } }),
  summary: (empId, fy) => api.get(`/employees/${empId}/kpi-summary`, { params: { fy } }),
  getDefinitions: (empId, fy) => api.get(`/employees/${empId}/kpi-definitions`, { params: { fy } }),
  setDefinitions: (empId, data) => api.post(`/employees/${empId}/kpi-definitions`, data),
  lockDefinitions: (empId, fy, locked = true) => api.post(`/employees/${empId}/kpi-definitions/lock`, null, { params: { fy, locked } }),
  setScore: (empId, data) => api.post(`/employees/${empId}/kpi-scores`, data),
  saveTemplate: (data) => api.post('/kpi-templates', data),
  templatesGrouped: () => api.get('/kpi-templates/grouped'),
  // Is there exactly one active sheet per role? The duplicate that started
  // this sat in the database for weeks because only a log line noticed.
  templateDiagnostics: () => api.get('/kpi-templates/diagnostics'),
  reconcileTemplates: (dry_run = true) => api.post('/kpi-templates/reconcile', { dry_run }),
  orgRoles: () => api.get('/kpi-center/org-roles'),
  saveOrgRoles: (map) => api.post('/kpi-center/org-roles', { map }),
  deleteTemplate: (name) => api.delete('/kpi-templates', { params: { name } }),
  allocation: (params) => api.get('/kpi-allocation', { params }),
  autoAllocate: (data) => api.post('/kpi-allocation/auto', data),
  bulkAssign: (data) => api.post('/kpi-allocation/bulk', data),
  duplicateTemplate: (data) => api.post('/kpi-templates/duplicate', data),
  copyKpis: (data) => api.post('/kpi-templates/copy-kpis', data),
  templateEmployees: (params) => api.get('/kpi-templates/employees', { params }),
  masterUsage: () => api.get('/kpi-master/usage'),
  computePreview: (params) => api.get('/kpi-master/compute-preview', { params }),
  nextPending: (params) => api.get('/kpi-next-pending', { params }),
  review: (params) => api.get('/kpi-review', { params }),
  compliance: (params) => api.get('/kpi-compliance', { params }),
  manualPreview: (params) => api.get('/kpi-manual/preview', { params }),
  manualHrQueue: (params) => api.get('/kpi-manual/hr-queue', { params }),
  // KPI 2.0 — auto-derivation engine
  autoStatus: (fy) => api.get('/kpi-auto/status', { params: { fy } }),
  autoCompute: (data) => api.post('/kpi-auto/compute', data),
  attachKaizen: (fy) => api.post('/kpi-auto/attach-kaizen', null, { params: { fy } }),
  ingest: (data) => api.post('/kpi-ingest', data),
  // A month arrives as a FILE exported from the source app — there is no fetch
  // and nothing to configure. The file names its own app, year and month, so
  // `payload` is the only thing that has to be right.
  uploadMonth: (data) => api.post('/kpi-upload', data),
  // Targets a KPI is scored against, per unit. The marking ladder is derived
  // from the target, so this is the only number anybody edits.
  unitTargets: () => api.get('/kpi-master/unit-targets'),
  saveUnitTargets: (key, data) => api.put(`/kpi-master/unit-targets/${encodeURIComponent(key)}`, data),
  monthlyActuals: (params) => api.get('/kpi-auto/monthly-actuals', { params }),
  plantHeadActuals: (params) => api.get('/kpi-auto/plant-head-actuals', { params }),
  addPlantHeadRows: (data) => api.post('/kpi-auto/plant-head-rows', data),
  savePlantHeadManual: (data) => api.post('/kpi-auto/plant-head-manual', data),
  employeeFill: (data) => api.post('/kpi-auto/employee-fill', data),
  master: () => api.get('/kpi-master'),
  saveMaster: (data) => api.post('/kpi-master', data),
  deleteMaster: (key) => api.delete(`/kpi-master/${encodeURIComponent(key)}`),
  // What pressing delete would actually do. Read-only; writes nothing.
  deleteImpact: (key) => api.get(`/kpi-master/${encodeURIComponent(key)}/delete-impact`),
  sourceFields: (params) => api.get('/kpi-master/source-fields', { params }),
  // Metric catalogue — what the three sources can actually supply, proven by
  // the last pull. Feeds both the Operations Data browser and the KPI metric
  // picker, because they are the same list read two ways.
  metricCatalog: (params) => api.get('/kpi/metric-catalog', { params }),
  renameMetric: (id, data) => api.put(`/kpi/metric-catalog/${id}`, data),
  // Publish a figure built on the Source Data screen into the SAME catalogue
  // the pulled numbers live in — so a figure somebody assembled is findable in
  // the list it was assembled from, and refreshes with every later import.
  buildMetric: (data) => api.post('/kpi/metric-catalog/built', data),
  // Try a marking ladder before saving it. Runs the REAL scorer server-side —
  // there is deliberately no second copy of the rule in JavaScript.
  previewBands: (data) => api.post('/kpi/bands/preview', data),
  // What a figure — or one figure over another — works out to per unit, run
  // through the REAL scorer so the preview cannot disagree with the mark.
  previewFormula: (data) => api.post('/kpi/formula/preview', data),
  // Build a 0-4 ladder around a figure the source already publishes, so a KPI
  // that reads a number every month stops scoring nothing. Derived, never
  // saved — the caller still presses Save, and what comes back is provisional.
  suggestLadder: (data) => api.post('/kpi-master/suggest-ladder', data),
  // Write a ladder to ONE scope: the shared default, or named plants only.
  // Deliberately not part of saveMaster — that posts the whole record, so a
  // screen showing one plant's ladder would send `bands_by_unit` back wholesale
  // and erase the overrides it never loaded.
  saveBandsForScope: (key, data) =>
    api.put(`/kpi-master/${encodeURIComponent(key)}/bands`, data),
  // Read-only provenance trace for the Source Data audit view.
  sourceAudit: (params) => api.get('/kpi/source-audit', { params }),
  // Which imported numbers a KPI actually scores, and which nothing reads.
  // Read-only: it creates no binding and changes no scoring.
  sourceUsage: (params) => api.get('/kpi/source-usage', { params }),
  // The KPIs every employee is measured on, whatever their role.
  mandatoryKpis: () => api.get('/kpi-master/mandatory'),
  saveMandatoryKpis: (kpi_keys) => api.put('/kpi-master/mandatory', { kpi_keys }),
  // ONE KPI on ONE person's sheet. Separate from setDefinitions, which rewrites
  // the whole sheet and re-mints every kpi_id — marks attach to kpi_id, so
  // using it to add a single row would detach that employee's existing marks.
  addKpiRow: (empId, data) => api.post(`/employees/${empId}/kpi-rows`, data),
  removeKpiRow: (empId, kpiId, params) =>
    api.delete(`/employees/${empId}/kpi-rows/${kpiId}`, { params }),
  saveUnitMap: (map) => api.post('/kpi-auto/unit-map', { map }),
  fillObjectives: (data) => api.post('/kpi-auto/fill-objectives', data),
  getObjectives: (params) => api.get('/kpi-auto/objectives', { params }),
  schedule: (params) => api.get('/kpi-auto/schedule', { params }),
  captureNow: () => api.post('/kpi-auto/capture-now'),
  runMonth: (data) => api.post('/kpi-auto/run-month', data),
  unfreezeMonth: (fy, month) => api.post('/kpi-auto/unfreeze', null, { params: { fy, month } }),
  buildRaw: (data) => api.post('/kpi-auto/build-raw', data),
  adminReview: (params) => api.get('/kpi-auto/admin-review', { params }),
  maintVerify: (params) => api.get('/kpi/machine-maintenance', { params }),
  getRaw: (params) => api.get('/kpi-auto/raw', { params }),
  saveRawTargets: (targets) => api.post('/kpi-auto/raw-targets', { targets }),
};

export const expenseApi = {
  // Every call carries the plant. The register used to be keyed by month
  // alone, so all units read and wrote the same rows.
  list: (unit) => api.get('/expense', { params: { unit } }),
  preview: (formData) => api.post('/expense/preview', formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }),
  commit: (data) => api.post('/expense/commit', data),
  saveMonth: (data) => api.post('/expense/month', data),
  deleteMonth: (ym, unit) => api.delete('/expense/month', { params: { ym, unit } }),
  claim: (data) => api.post('/expense/claim', data),
};

// Requirement TAT — how long a seat stayed empty. Read-only on purpose: every
// figure is derived from the vacancy's own dates and the replacement's joining
// date, so there is nothing to post.
export const requirementTatApi = {
  list: (params) => api.get('/requirement-tat', { params }),
};

export const complaintSheetApi = {
  sheet: (params) => api.get('/customer-complaints/sheet', { params }),
};

export const customerApi = {
  // The customer master, read live from Operations — never a second local list.
  list: (params) => api.get('/customers', { params }),
};

export const customerScoreApi = {
  list: () => api.get('/customer-scores'),
  save: (data) => api.post('/customer-scores', data),
  remove: (id) => api.delete(`/customer-scores/${id}`),
};

export const complaintApi = {
  list: () => api.get('/customer-complaints'),
  add: (data) => api.post('/customer-complaints', data),
  remove: (id) => api.delete(`/customer-complaints/${id}`),
};

export const accidentApi = {
  // Unit-scoped: an accident belongs to one plant and must not appear on, or
  // score against, another.
  list: (unit) => api.get('/accidents', { params: { unit } }),
  add: (data) => api.post('/accidents', data),
  remove: (id) => api.delete(`/accidents/${id}`),
};

export const attritionApi = {
  list: (unit) => api.get('/attrition', { params: { unit } }),
  saveMonth: (data) => api.post('/attrition/month', data),
  deleteMonth: (ym, unit) => api.delete('/attrition/month', { params: { ym, unit } }),
  // Associates are typed by HR. Its own call on purpose: Staff is derived from
  // the employee records and nothing on this screen may write to it.
  saveAssociates: (data) => api.post('/attrition/associates', data),
};

export const trainingPlanApi = {
  list: (unit) => api.get('/training-plan', { params: { unit } }),
  saveMonth: (data) => api.post('/training-plan/month', data),
  deleteMonth: (ym, unit) => api.delete('/training-plan/month', { params: { ym, unit } }),
};

export const recruitmentTatApi = {
  list: () => api.get('/recruitment-tat'),
  add: (data) => api.post('/recruitment-tat', data),
  remove: (id) => api.delete(`/recruitment-tat/${id}`),
};

export const lateApi = {
  // Reporting time + the penalty band table. The register reads the rule from
  // here instead of restating it, so the slab shown while typing is the slab
  // the server stores.
  policy: () => api.get('/attendance/reporting-time'),
  add: (data) => api.post('/late-entries', data),
  byDate: (date) => api.get('/late-entries', { params: { date } }),
  byMonth: (fy, month) => api.get('/late-entries', { params: { fy, month } }),
  remove: (id) => api.delete(`/late-entries/${id}`),
  // Sync Now. The website cannot reach the punch machine — it raises a flag,
  // the office agent (already polling) fetches and pushes through the existing
  // ingest, and the page watches for the completion stamp.
  syncRequest: () => api.post('/attendance/sync-request'),
  syncStatus: () => api.get('/attendance/sync-status'),
};

export const documentApi = {
  // Keep in sync with backend MAX_DOC_UPLOAD_BYTES (server.py) so oversize
  // files fail with a friendly toast before wasting a full multipart round-trip.
  MAX_UPLOAD_BYTES: 50_000_000,
  list: (empId) => api.get(`/employees/${empId}/documents`),
  upload: (empId, docType, file) => {
    if (file && file.size > documentApi.MAX_UPLOAD_BYTES) {
      const mb = Math.floor(documentApi.MAX_UPLOAD_BYTES / 1_000_000);
      return Promise.reject({
        response: { data: { detail: `File too large (max ${mb} MB)` }, status: 400 },
      });
    }
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/employees/${empId}/documents?doc_type=${docType}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  download: (docId) => `${API}/documents/${docId}/download`,
  remove: (docId) => api.delete(`/documents/${docId}`),
  summary: (params) => api.get('/documents/summary', { params }),
  pending: (params) => api.get('/documents/pending', { params }),
  preview: (docId) => api.get(`/documents/${docId}/download`, { responseType: 'blob' }),
};

// Staff induction. The document itself rides on documentApi with the doc_type
// the backend expects, so there is one upload path and one storage location.
export const inductionApi = {
  DOC_TYPE: 'Staff Induction',
  get: (empId) => api.get(`/employees/${empId}/induction`),
  save: (empId, data) => api.put(`/employees/${empId}/induction`, data),
  register: (params) => api.get('/induction/register', { params }),
};

export const preJoinerApi = {
  create: (data) => api.post('/pre-joiners', data),
  list: (params) => api.get('/pre-joiners', { params }),
  join: (id) => api.post(`/pre-joiners/${id}/join`),
  refuse: (id, reason) => api.post(`/pre-joiners/${id}/refuse`, { reason }),
  offerLetter: (id, data) => api.post(`/pre-joiners/${id}/offer-letter`, data),
  remove: (id) => api.delete(`/pre-joiners/${id}`),
};

// The shop floor. A separate register from `employeeApi` (staff) on purpose —
// see the ASSOCIATES section in backend/server.py.
export const associateApi = {
  // Everything the profile popup shows, in one call keyed on the associate's
  // id. One call so the popup cannot render a half-loaded mixture of two
  // people, and by ID because two associates can share a name.
  profile: (id) => api.get(`/associates/${id}/profile`),
  // `params` carries unit, contractor_id, department, designation, status, q,
  // sort and direction. Undefined keys are dropped by axios, so the caller can
  // pass the whole filter object and let the server apply its defaults.
  list: (params) => api.get('/associates', { params }),
  // Contractors, departments, designations and units for the pickers, in one
  // round trip — the add row needs all four before it can be used.
  meta: () => api.get('/associates/meta'),
  get: (id) => api.get(`/associates/${id}`),
  add: (data) => api.post('/associates', data),
  update: (id, data) => api.put(`/associates/${id}`, data),
  // Somebody who worked and then left. Keeps the record so attrition can
  // count it; the server dates it today when `last_working_day` is omitted.
  exit: (id, data = {}) => api.post(`/associates/${id}/exit`, data),
  // A row typed by mistake. Erases it outright — use `exit` for a real leaver.
  remove: (id) => api.delete(`/associates/${id}`),
  // The Skill Matrix assessment. GET brings the matrices this person's
  // designation requires, where they stand and every past assessment; POST
  // scores one. There is no "set level" call anywhere — the level is derived
  // from the marks and from nothing else.
  assessment: (id) => api.get(`/associates/${id}/skill-assessment`),
  saveAssessment: (id, data) => api.post(`/associates/${id}/skill-assessment`, data),
};

// The fixed Skill Matrix pool, and which of them each approved Associate
// designation is assessed on. One canonical record per topic — a designation
// REFERS to it, so there is never a second copy under a near-identical name.
export const skillMatrixApi = {
  pool: () => api.get('/skill-matrices'),
  // Adding a skill is configuration, not a code change — this is what the
  // Skills Bank in Masters calls.
  create: (data) => api.post('/skill-matrices', data),
  update: (key, data) => api.put(`/skill-matrices/${key}`, data),
  // Deactivates rather than deletes when assessments already reference the
  // skill; the response says which happened.
  remove: (key) => api.delete(`/skill-matrices/${key}`),
  map: () => api.get('/associate-skill-map'),
  saveMap: (designation, matrix_keys) =>
    api.put('/associate-skill-map', { designation, matrix_keys }),
};

// The shop-floor designation pool. Its own master rather than part of the
// staff `designations` list: that one carries a `level` driving the org
// ladder, and folding fifty shop-floor roles in would put SWEEPER in the
// picker HR appoints a manager from. Both are set in Settings → Masters.
export const associateDesignationApi = {
  list: () => api.get('/associate-designations'),
  add: (name) => api.post('/associate-designations', { name }),
  update: (id, data) => api.put(`/associate-designations/${id}`, data),
  // Fold one spelling into another. Renaming cannot do it — that is refused
  // as a duplicate — so this is the only way to heal a split like
  // "BIN WASHING" vs "BINWASHING".
  merge: (from_name, into_name) => api.post('/associate-designations/merge', { from_name, into_name }),
  remove: (id) => api.delete(`/associate-designations/${id}`),

  // Folding the legacy pool into the thirteen approved designations. Read
  // first, write only on an explicit `apply` — the preview is a plain GET so
  // it can be opened and re-opened without touching a single record.
  migrationPreview: () => api.get('/associate-designations/migration-preview'),
  // `overrides` carries the decisions the approved sheet does not make, e.g.
  // { SWEEPER: 'HOUSE KEEPER' }. Without them those rows block the migration
  // rather than being guessed at.
  migrate: (apply, overrides = {}) =>
    api.post('/associate-designations/migrate', { apply, overrides }),
};

// Moving the whole associates module between servers. The register was built
// from attendance workbooks that only exist on the machine they were loaded
// on, so the live server receives it as one json file instead.
export const associateBundleApi = {
  export: () => api.get('/associates/export'),
  // Dry run by default — the caller must pass apply:true deliberately.
  import: (bundle, { apply = false, on_conflict = 'skip' } = {}) =>
    api.post('/associates/import', { bundle, apply, on_conflict }),
};

export const contractorApi = {
  list: (unit) => api.get('/contractors', { params: { unit } }),
  add: (data) => api.post('/contractors', data),
  update: (id, data) => api.put(`/contractors/${id}`, data),
  remove: (id) => api.delete(`/contractors/${id}`),
};

export const masterApi = {
  units: () => api.get('/units'),
  departments: () => api.get('/departments'),
  designations: () => api.get('/designations'),
  createUnit: (name, code) => api.post('/units', null, { params: { name, code } }),
  createDepartment: (name) => api.post('/departments', null, { params: { name } }),
  createDesignation: (name) => api.post('/designations', null, { params: { name } }),
  addUnit: (name) => api.post(`/units?name=${encodeURIComponent(name)}`),
  renameUnit: (id, name) => api.put(`/units/${id}`, { name }),
  mergeUnits: (from_name, into_name) => api.post('/units/merge', { from_name, into_name }),
  deleteUnit: (id) => api.delete(`/units/${id}`),
  setUnitLetterhead: (id, data) => api.put(`/units/${id}/letterhead`, data),
  addDepartment: (name) => api.post(`/departments?name=${encodeURIComponent(name)}`),
  renameDepartment: (id, name) => api.put(`/departments/${id}`, { name }),
  // Put a department inside a group (Quality holds QA, QC, SQA, Standard Room)
  // or take it out again with null. No employee record changes.
  setDepartmentParent: (id, parent) => api.put(`/departments/${id}/parent`, { parent: parent || null }),
  // Who owns one section of a department, in one unit. `employee_id: null` clears it.
  setSectionOwner: (id, section, unit, employee_id) =>
    api.put(`/departments/${id}/section-owner`, { section, unit, employee_id: employee_id || null }),
  mergeDepartments: (from_name, into_name) => api.post('/departments/merge', { from_name, into_name }),
  deleteDepartment: (id) => api.delete(`/departments/${id}`),
  addDesignation: (name) => api.post(`/designations?name=${encodeURIComponent(name)}`),
  updateDesignation: (id, name, level) => api.put(`/designations/${id}`, { name, level }),
  mergeDesignations: (from_name, into_name) => api.post('/designations/merge', { from_name, into_name }),
  deleteDesignation: (id) => api.delete(`/designations/${id}`),
  applyLevels: () => api.post('/designations/apply-levels'),
};

export const orgchartApi = {
  // The chart draws only the levels configured as visible (L5–L15 by default).
  // `all_levels: true` asks for the whole L1–L15 hierarchy — used where a count
  // of people must be complete rather than chart-shaped.
  get: (params) => api.get('/orgchart', { params }),
  addNode: (data) => api.post('/orgchart/add-node', data),
  levelVisibility: () => api.get('/orgchart/level-visibility'),
  setLevelVisibility: (data) => api.put('/orgchart/level-visibility', data),
};

// Education history. Records live on the employee document; their order is
// the display order, so reorder just persists the new sequence of ids.
export const educationApi = {
  list: (empId) => api.get(`/employees/${empId}/education`),
  add: (empId, data) => api.post(`/employees/${empId}/education`, data),
  update: (empId, recId, data) => api.put(`/employees/${empId}/education/${recId}`, data),
  remove: (empId, recId) => api.delete(`/employees/${empId}/education/${recId}`),
  reorder: (empId, ids) => api.put(`/employees/${empId}/education-order`, { ids }),
};

export const letterApi = {
  list: () => api.get('/letters'),
  removeRegister: (kind, id) => api.delete(`/letters/register/${kind}/${id}`),
};

// The QR token identifies the employee; the session token proves who they are.
// Sessions live in sessionStorage so a shared shop-floor phone does not stay
// signed in after the browser is closed.
const sessKey = (token) => `portal_session_${token}`;
export const portalSession = {
  get: (token) => sessionStorage.getItem(sessKey(token)) || '',
  set: (token, v) => sessionStorage.setItem(sessKey(token), v || ''),
  clear: (token) => sessionStorage.removeItem(sessKey(token)),
};
const auth = (token) => ({ headers: { Authorization: `Bearer ${portalSession.get(token)}` } });

export const kpiPortalApi = {
  // --- open, pre-login ---
  authState: (token) => axios.get(`${API}/kpi-portal/${token}/auth-state`),
  sendOtp: (token, purpose, channel) => axios.post(`${API}/kpi-portal/${token}/otp/send`, { purpose, channel }),
  verifyOtp: (token, purpose, code) => axios.post(`${API}/kpi-portal/${token}/otp/verify`, { purpose, code }),
  setPassword: (token, ticket, password) => axios.post(`${API}/kpi-portal/${token}/password/set`, { ticket, password }),
  login: (token, password) => axios.post(`${API}/kpi-portal/${token}/login`, { password }),

  // --- require a session ---
  get: (token) => axios.get(`${API}/kpi-portal/${token}`, auth(token)),
  manualKpi: (token, params) => axios.get(`${API}/kpi-portal/${token}/manual-kpi`, { params, ...auth(token) }),
  submitManualKpi: (token, data) => axios.post(`${API}/kpi-portal/${token}/manual-kpi`, data, auth(token)),
  submit: (token, data) => axios.post(`${API}/kpi-portal/${token}`, data, auth(token)),
  suggestion: (token, data) => axios.post(`${API}/kpi-portal/${token}/suggestion`, data, auth(token)),
  mySuggestions: (token) => axios.get(`${API}/kpi-portal/${token}/suggestions`, auth(token)),
  // One filing: a suggestion, a kaizen, or both, from one screen and one press.
  submission: (token, data) => axios.post(`${API}/kpi-portal/${token}/submission`, data, auth(token)),
  kaizen: (token, data) => axios.post(`${API}/kpi-portal/${token}/kaizen`, data, auth(token)),
  myKaizens: (token) => axios.get(`${API}/kpi-portal/${token}/kaizens`, auth(token)),
  // Their own kaizen on the printed HR-F-16 sheet.
  kaizenSheet: (token, kid, logoUrl) => axios.get(
    `${API}/kpi-portal/${token}/kaizens/${kid}/sheet`,
    { params: { logo_url: logoUrl }, ...auth(token) }),
  // Which months already carry a suggestion or a kaizen — one of the two is
  // required every month, and the KPI sheet is refused without it.
  participation: (token) => axios.get(`${API}/kpi-portal/${token}/participation`, auth(token)),
  report: (token) => axios.get(`${API}/kpi-portal/${token}/report`, auth(token)),
  // Read-only "My KPI Score" — server resolves the employee from the token.
  myKpi: (token, month) => axios.get(`${API}/kpi-portal/${token}/my-kpi`,
    { params: month ? { month } : {}, ...auth(token) }),
};

export const adminApi = {
  backup: (opts = {}) => api.post('/admin/backup', null, { params: opts }),
  restore: (data, opts = {}) => api.post('/admin/restore', data, { params: opts }),
  rbacAudit: () => api.get('/admin/rbac-audit'),
  salaryCycles: () => api.get('/salary-cycles'),
  createSalaryCycle: (data) => api.post('/admin/salary-cycle', data),
  lockSalaryCycle: (year) => api.post(`/admin/salary-cycle/${year}/lock`),
  unlockSalaryCycle: (year) => api.post(`/admin/salary-cycle/${year}/unlock`),
  kpiTokens: (params) => api.get('/admin/kpi-tokens', { params }),
  stagePromotion: (year, data) => api.post(`/admin/salary-cycle/${year}/stage-promotion`, data),
  unstagePromotion: (year, empId) => api.delete(`/admin/salary-cycle/${year}/stage-promotion/${empId}`),
  stagedPromotions: (year) => api.get(`/admin/salary-cycle/${year}/staged-promotions`),
  letterPreview: (year, unit) => api.get(`/admin/salary-cycle/${year}/letter-preview`, { params: { unit } }),
  bulkLetters: (year, data) => api.post(`/admin/salary-cycle/${year}/bulk-letters`, data || {}),
  nextDesignations: (level) => api.get('/promotions/next-options', { params: { level } }),
};

export const emailApi = {
  status: () => api.get('/email/status'),
  templates: () => api.get('/email/templates'),
  saveTemplate: (key, data) => api.put(`/email/templates/${key}`, data),
  recipients: () => api.get('/email/recipients'),
  // HR's CC list per occasion group, stored in app_config and read at send time.
  occasionCc: () => api.get('/email/occasion-cc'),
  saveOccasionCc: (groups) => api.put('/email/occasion-cc', { groups }),
  send: (data) => api.post('/email/send', data),
  log: (params) => api.get('/email/log', { params }),
  runAutomation: () => api.post('/email/run-automation'),
  test: (to) => api.post('/email/test', { to }),
  // Monthly employee exit report. `preview` never sends and never consumes the
  // month's dedupe key; `sendExitReport` with a testTo does the same.
  exitReportPreview: (month) => api.get('/email/exit-report/preview', { params: { month } }),
  sendExitReport: (month, testTo) =>
    api.post('/email/exit-report/send', null, { params: { month, test_to: testTo || undefined } }),
};

export const signatureApi = {
  get: () => api.get('/admin/signature'),
  requestOtp: () => api.post('/letters/sign-otp'),
  verifyOtp: (code) => api.post('/letters/sign-verify', { code }),
  audit: () => api.get('/letters/signature-audit'),
  upload: (file, name, title, signatoryEmail) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/admin/signature', fd, {
      params: { name, title, signatory_email: signatoryEmail },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  remove: () => api.delete('/admin/signature'),
};

export const docReqApi = {
  list: () => api.get('/doc-requirements'),
  add: (name, mandatory = false) => api.post('/doc-requirements', { name, mandatory }),
  update: (id, data) => api.put(`/doc-requirements/${id}`, data),
  remove: (id) => api.delete(`/doc-requirements/${id}`),
};

export default api;

// Supporting evidence for a suggestion or a kaizen. Uploaded on its own and
// then referenced on the record, so a PDF never has to be base64'd into the
// submission JSON.
export const submissionApi = {
  uploadAttachment: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/submissions/attachment', fd,
      { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

export const suggestionApi = {
  list: (empId, period) => api.get(`/employees/${empId}/suggestions`, { params: { period } }),
  // `body` carries text + title + benefit_category + benefit_amount +
  // attachment. The old two-argument shape (empId, "some text") still works so
  // nothing that called it before has to change at once.
  add: (empId, body, period) => api.post(`/employees/${empId}/suggestions`,
    typeof body === 'string' ? { text: body, period } : { period, ...body }),
  review: (id, data) => api.post(`/suggestions/${id}/review`, data),
  history: (empId) => api.get(`/employees/${empId}/suggestions/history`),
  monthly: (params) => api.get('/suggestions/monthly', { params }),
  summary: (params) => api.get('/suggestions/summary', { params }),
};

export const kaizenApi = {
  list: (params) => api.get('/kaizens', { params }),
  monthly: (params) => api.get('/kaizens/monthly', { params }),
  summary: (params) => api.get('/kaizens/summary', { params }),
  create: (data) => api.post('/kaizens', data),
  update: (id, data) => api.put(`/kaizens/${id}`, data),
  remove: (id) => api.delete(`/kaizens/${id}`),
  print: (id, data) => api.post(`/kaizens/${id}/print`, data),
  // Photos and videos upload on their own so a clip never has to be base64'd
  // into the kaizen JSON. Returns { url, kind }.
  uploadMedia: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/kaizens/media', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};


export const aiApi = {
  status: () => api.get('/ai/status'),
  // The API key stays on the server — the browser only ever sends the question.
  chat: (message, history) => api.post('/ai/chat', { message, history }),
};


export const letterRegApi = {
  list: (empId) => api.get(`/employees/${empId}/letters`),
  // Read-only: has this employee completed probation, and when do they?
  confirmationEligibility: (empId) =>
    api.get(`/employees/${empId}/confirmation-eligibility`),
  // Employees newly eligible for a confirmation letter (none issued yet).
  confirmationDue: (windowDays) =>
    api.get('/confirmation-due', { params: windowDays ? { window_days: windowDays } : {} }),
  allLetters: (empId) => api.get(`/employees/${empId}/all-letters`),
  add: (empId, data) => api.post(`/employees/${empId}/letters`, data),
  remove: (id) => api.delete(`/letters/${id}`),
  print: (id, data) => api.post(`/letters/${id}/print`, data),
  // Renders a transfer letter without filing it, so preview leaves no record.
  previewTransfer: (empId, data) => api.post(`/employees/${empId}/transfer-letter-preview`, data),
  // Employees who may sign a letter — Assistant Manager and above.
  signatories: () => api.get('/signatories'),
  reprintPromotion: (id, data) => api.post(`/promotions/${id}/reprint`, data),
  reprintTransfer: (id, data) => api.post(`/transfer-letters/${id}/reprint`, data),
  reprintOffer: (id, data) => api.post(`/offer-letters/${id}/reprint`, data),
};

export const rbacApi = {
  catalog: () => api.get('/rbac/catalog'),
  roles: () => api.get('/rbac/roles'),
  createRole: (data) => api.post('/rbac/roles', data),
  updateRole: (role, data) => api.put(`/rbac/roles/${role}`, data),
  deleteRole: (role) => api.delete(`/rbac/roles/${role}`),
};

export const userApi = {
  list: () => api.get('/users'),
  create: (data) => api.post('/auth/register', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  resetPassword: (id, new_password) => api.post(`/users/${id}/reset-password`, { new_password }),
  remove: (id) => api.delete(`/users/${id}`),
};
